import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useSelector } from 'react-redux';
import arrayMove from '../../../../common/utility/arrayMove';
import { RootState } from '../../../../redux/store';
import {
  customise,
  fetchNotes,
  getAnalysisContent,
  INote,
} from '../../../../common/services/http/analysisContentService';
import { parseInt } from 'lodash';

import Widget from '../widget/widget';
import CustomisationGuide from '../customisationGuide/customisationGuide';
import BoardsDropdown from '../boardsDropdown/boardsDropdown';
import OutletLoader from '../../../ui/loaders/outletLoader/outletLoader';

import '../../pages/boards/board.scss';
import { AnalysisContentWidgetModel } from '../../../../models/AnalysisContentWidgetModel';
import { AnalysisContentModel } from '../../../../models/AnalysisContentModel';
import AnalysisContentObjectModel from '../../../../models/AnalysisContentObjectModel';
import { ITimePeriod } from '../../../../models/types/ITimePeriod';

const Board: React.FC = () => {
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedObject, setSelectedObject] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [widgets, setWidgets] = useState<AnalysisContentWidgetModel[]>([]);
    const [initialWidgets, setInitialWidgets] = useState<AnalysisContentWidgetModel[]>([]);
    const [initialWidgetsForCancel, setInitialWidgetsForCancel] = useState<AnalysisContentWidgetModel[]>([]);
    const [newIndex, setNewIndex] = useState(null);
    const [oldIndex, setOldIndex] = useState(null);
    const [widgetToMove, setWidgetToMove] = useState(null);
    const [reset, setReset] = useState(false);
    const [customisation, setCustomisation] = useState(false);
    const [objects, setObjects] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [measures, setMeasures] = useState([]);
    const [staticProperties, setStaticProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [notes, setNotes] = useState([]);
    const [suppressLocalCurrency, setSuppressLocalCurrency] = useState(false);

    const investor = useSelector((state: RootState) => state.investor.investor);
    let { boardId, investorId } = useParams();
    const location = useLocation();
    const passedObject = location.state && location.state['data'];

    const setDropdownsData = (res: AnalysisContentModel): void => {
      const objectsArray = [];
      const periodsArray = [];
      const scenariosArray = [];

      res.objects.map((obj: AnalysisContentObjectModel) => {
        objectsArray.push(obj);
      });
      setObjects(objectsArray);

      res.timePeriods.map((obj: ITimePeriod) => {
        periodsArray.push(obj);
      });
      setPeriods(periodsArray);

      res.scenarios.map((obj) => {
        scenariosArray.push(obj);
      });
      setScenarios(scenariosArray);
    };

    const filterNotesByObject = (data: INote[], object: AnalysisContentObjectModel): void => {
      let tempNotesArray = [];
      data.map((note) => {
        for (let i = 0; i < note.objects.length; i++) {
          if (note.objects[i].contentObjectId === object.id) {
            tempNotesArray.push(note);
          }
        }
      });
      setFilteredNotes(tempNotesArray);
    };

    const onPeriodChange = (event: DropDownListChangeEvent): void => {
      if (event.value) {
        setSelectedPeriod(event.value);
      }
    };

    const onObjectChange = (event: DropDownListChangeEvent): void => {
      if (event.value) {
        setSelectedObject(event.value);
      }
    };

    const onResetChange = (e) => {
      setReset(e);
    };

    const onCustomisationChange = (e) => {
      if (e === true) {
        setInitialWidgetsForCancel([...widgets]);
      }
      setCustomisation(e);
    };

    const saveCustomisation = (boardId) => {
      customise(boardId, widgets).then(() => {
      });
    };

    const resetCustomisation = (boardId) => {
      const widgetsReset = initialWidgets.map((widget) => {
        return { ...widget, visible: true };
      });
      customise(boardId, widgetsReset).then(() => {
      });
    };

    const handleWidgetMove = (e) => {
      setWidgetToMove(e);
    };

    const handleCancel = () => {
      setWidgets([...initialWidgetsForCancel]);
    };

    const handleWidgetChange = (e) => {
      setWidgets(e);
    };

    const changeNewIndex = (e) => {
      setNewIndex(e);
    };

    const changeOldIndex = (e) => {
      setOldIndex(e);
    };

    const onCurrenciesChange = (event: DropDownListChangeEvent): void => {
      if (event.value) {
        setSelectedCurrency(event.value);
      }
    };

    const handlePinWidget = (e) => {
      let pinnedWidgets = widgets.map((widget) => {
        return {...widget, pinned: [...widget.pinned, e]}
      });
      setWidgets(pinnedWidgets);
    }

    const handleUnpinWidget = (e) => {
      let pinnedWidgets = widgets.map((widget) => {
        let pinnedArray = widget.pinned.filter((pinnedWidget) => !(pinnedWidget.investorObjectId === e.investorObjectId && pinnedWidget.objectPath === e.objectPath));
        return {...widget, pinned: pinnedArray}
      });
      setWidgets(pinnedWidgets);
    }

    const generateCurrencies = (object, scenarios, suppressLocalCurrency) => {
      let currenciesArray = [];
      if (object) {
        object.nodesOfObject.forEach((node) => {
          let scenario = scenarios.find((scenario) => {
            return scenario.id === node.scenarioId;
          });
          if (scenario) {
            scenario.currencies.forEach((currency) => {
              if (!currenciesArray.find((curr) => {
                return curr.currency === currency.currency;
              }))
                currenciesArray.push(currency);
            });
          }
        });
      }
      if (suppressLocalCurrency) {
        return currenciesArray.filter((curr) => {
          return curr.currency !== '_';
        });
      } else {
        return currenciesArray;
      }
    };

    useEffect(() => {
      if (selectedPeriod && boardId) {
        fetchNotes(parseInt(boardId), selectedPeriod.id).then((res) => {
          setNotes(res);
          filterNotesByObject(res, selectedObject);
        });
      }
    }, [selectedPeriod]);

    useEffect(() => {
      let newArray = arrayMove(widgets, oldIndex, newIndex);
      setWidgets(newArray);
    }, [oldIndex, newIndex, widgetToMove]);

    useEffect(() => {
      filterNotesByObject(notes, selectedObject);
      const currenciesArray = generateCurrencies(selectedObject, scenarios, suppressLocalCurrency);
      setCurrencies(currenciesArray);
      setSelectedCurrency(currenciesArray[0]);
    }, [selectedObject]);

    useEffect(() => {
      setLoading(true);
      investorId &&
      getAnalysisContent(parseInt(boardId), parseInt(investorId))
        .then((res: AnalysisContentModel) => {
          if (passedObject) {
            const object = res.objects.find((item) => {
              return item.objectPath === passedObject;
            });
            if (object) {
              setSelectedObject(object);
            } else {
              setSelectedObject(res.objects[0]);
            }
          } else {
            setSelectedObject(res.objects[0]);
          }
          setSuppressLocalCurrency(res.suppressLocalCurrency);
          const currenciesArray = generateCurrencies(res.objects[0], res.scenarios, res.suppressLocalCurrency);
          setSelectedCurrency(currenciesArray[0]);
          let lastPeriodIndex = res.timePeriods.length - 1;
          setSelectedPeriod(res.timePeriods[lastPeriodIndex]);

          setInitialWidgets(res.widgets);

          const widgetData = res.userContent
            ? res.userContent.widgets.map((widget) => {
              let widgetObject = {};
              res.widgets.map((widgetItem) => {
                if (widgetItem.id === widget.widgetId) {
                  widgetObject = { ...widgetItem, visible: widget.visible };
                }
              });
              return widgetObject;
            })
            : res.widgets.map((widget) => {
              return { ...widget, visible: true };
            });

          setWidgets(widgetData);
          setMeasures(res.measures);
          setStaticProperties(res.staticProperties);
          setDropdownsData(res);
          setLoading(false);
        })
        .catch((e) => console.log('broke analysis content fetch', e, ' on investor id ', investor.id));
    }, [boardId, investorId, reset]);

    return (
      <>
        {loading ? (
          <OutletLoader />
        ) : (
          <>
            <BoardsDropdown
              loading={loading}
              objects={objects}
              periods={periods}
              currencies={currencies}
              selectedObject={selectedObject}
              selectedCurrency={selectedCurrency}
              onObjectChange={onObjectChange}
              onPeriodChange={onPeriodChange}
              onCurrenciesChange={onCurrenciesChange}
              onCustomisationChange={onCustomisationChange}
              filteredNotes={filteredNotes}
            />

            <div className='boards-grid-container'>
              {widgets &&
                widgets.map(
                  (widget) =>
                    widget && (
                      <Widget
                        scenarios={scenarios}
                        widget={widget}
                        measures={measures}
                        key={widget.id}
                        periods={periods}
                        notes={filteredNotes}
                        boardId={parseInt(boardId)}
                        id={widget.id}
                        staticProperties={staticProperties}
                        customisation={customisation}
                        widgets={widgets}
                        handleWidgetChange={handleWidgetChange}
                        handlePinWidget={handlePinWidget}
                        handleUnpinWidget={handleUnpinWidget}
                        handleWidgetMove={handleWidgetMove}
                        changeNewIndex={changeNewIndex}
                        changeOldIndex={changeOldIndex}
                        boardSelectedCurrency={selectedCurrency}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                      />
                    ),
                )}
            </div>
          </>
        )}
        <CustomisationGuide customisation={customisation} saveCustomisation={saveCustomisation}
                            handleCustomisation={onCustomisationChange} handleReset={onResetChange}
                            resetCustomisation={resetCustomisation} handleCancel={handleCancel} />
      </>
    );
  }
;

export default Board;
